import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CircularProgress from '@mui/material/CircularProgress';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import '../Customer/Customer.css';

function RoleIndex() {
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const hasEditAdministrationPermission = permissions.includes('Edit Administration');
    const hasDeleteAdministrationPermission = permissions.includes('Delete Administration');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchTableData();
    }, [isAuthenticated, companyId, token, logout]);

    const fetchTableData = async () => {
        setLoading(true);
        fetchData('RoleApi', `/${companyId}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setData(response.response_data);
                }
            })
            .catch((error) => {
                console.error('Error fetching Role Details:', error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleDelete = async () => {
        deleteData('RoleApi', `${deleteItemId}`)
            .then(() => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('RoleApi', `${itemId}`)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    return (
        <div className="customer-container">
            {loading && (
                <div className="loading-overlay">
                    <CircularProgress size={40} className="loading-spinner" />
                    <span className="loading-text">Loading...</span>
                </div>
            )}

            {/* Header */}
            <div className="page-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <AdminPanelSettingsIcon style={{ color: '#1e3a5f', fontSize: '28px' }} />
                    <h1 className="page-title">Roles</h1>
                </div>
                <div className="header-actions">
                    <div className="search-box">
                        <SearchIcon />
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search roles..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                <div className="table-wrapper">
                    <table className="customer-table">
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>#</th>
                                <th>Role Name</th>
                                <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length > 0 ? (
                                filteredData.map((item, index) => (
                                    <tr key={item.Id} className={item.IsActive ? '' : 'inactive-row'}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <span style={{ 
                                                display: 'inline-flex', 
                                                alignItems: 'center', 
                                                gap: '8px',
                                                fontWeight: '500'
                                            }}>
                                                <span style={{
                                                    width: '8px',
                                                    height: '8px',
                                                    borderRadius: '50%',
                                                    background: item.IsActive ? '#059669' : '#dc2626'
                                                }}></span>
                                                {item.Name}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                {hasEditAdministrationPermission && (
                                                    <Link to={`/roleDetail/${item.Id}`} className="btn-icon btn-edit" title="Edit Permissions">
                                                        <EditIcon fontSize="small" />
                                                    </Link>
                                                )}
                                                {hasDeleteAdministrationPermission && (
                                                    <button
                                                        onClick={() => item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)}
                                                        className={`btn-icon ${item.IsActive ? 'btn-delete' : 'btn-activate'}`}
                                                        title={item.IsActive ? 'Delete' : 'Activate'}
                                                    >
                                                        {item.IsActive ? <DeleteIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3">
                                        <div className="empty-state">
                                            <p>No roles found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
                <DialogTitle>Delete Role?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this role? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '16px' }}>
                    <button onClick={closeConfirmDialog} className="btn-cancel">
                        Cancel
                    </button>
                    <button onClick={handleDelete} className="btn-confirm-delete">
                        Delete
                    </button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default RoleIndex;
